import React, { useEffect } from 'react';
import {
    CheckCircle2,
    Rocket,
    Zap,
    Film,
    Mic2,
    Pen,
    TrendingUp,
    Layers,
    XCircle,
    Scale,
    Wand2,
    Calendar,
    Coins,
    Sparkles,
    Workflow,
    Handshake
} from 'lucide-react';

/* ─── Reusable Primitives ─── */

const SectionTag = ({ icon: IconComp, label, color = "indigo" }) => {
    const colorClasses = {
        indigo: "bg-indigo-500/10 border-indigo-500/20 text-indigo-300",
        rose: "bg-rose-500/10 border-rose-500/20 text-rose-300",
        emerald: "bg-emerald-500/10 border-emerald-500/20 text-emerald-300",
        amber: "bg-amber-500/10 border-amber-500/20 text-amber-300"
    };
    return (
        <div className={`inline-flex items-center gap-2 px-3 py-1 mb-6 rounded-full border text-sm font-semibold tracking-wide uppercase ${colorClasses[color]}`}>
            <IconComp className="w-4 h-4" /> {label}
        </div>
    );
};

const Check = ({ children, highlight = false }) => (
    <li className="flex gap-3 items-start">
        <CheckCircle2 className={`w-5 h-5 mt-0.5 flex-shrink-0 ${highlight ? 'text-indigo-400' : 'text-emerald-400'}`} />
        <span className={`leading-relaxed ${highlight ? 'text-indigo-100 font-medium' : 'text-slate-200 text-sm'}`}>{children}</span>
    </li>
);

const Cross = ({ children }) => (
    <li className="flex gap-3 items-start opacity-70">
        <XCircle className="w-5 h-5 mt-0.5 flex-shrink-0 text-rose-500" />
        <span className="leading-relaxed text-slate-300 text-sm">{children}</span>
    </li>
);

const StatCard = ({ value, label, sub, accent = "indigo" }) => {
    const colors = {
        indigo: "from-indigo-900/40 to-slate-900/80 border-indigo-500/30",
        emerald: "from-emerald-900/40 to-slate-900/80 border-emerald-500/30",
        purple: "from-purple-900/40 to-slate-900/80 border-purple-500/30",
        amber: "from-amber-900/40 to-slate-900/80 border-amber-500/30",
        rose: "from-rose-900/40 to-slate-900/80 border-rose-500/30"
    };
    return (
        <div className={`bg-gradient-to-br ${colors[accent]} border rounded-2xl p-6 text-center h-full flex flex-col justify-center`}>
            <div className="text-3xl md:text-4xl font-black text-white mb-1">{value}</div>
            <div className="text-sm font-semibold text-white/80">{label}</div>
            {sub && <div className="text-xs text-white/50 mt-2">{sub}</div>}
        </div>
    );
};


/* ─── Main Component ─── */
export default function LeosPrideProposalV2() {

    useEffect(() => {
        document.title = "Leo's Pride | Production Retainer & Partnership v2";
        window.scrollTo(0, 0);
    }, []);

    return (
        <div className="font-sans leading-relaxed text-white overflow-x-hidden selection:bg-indigo-500/30 pb-20 pt-24 relative z-10">

            {/* Floating Header */}
            <header className="fixed top-0 left-0 right-0 z-50 p-6 flex justify-between items-center bg-transparent pointer-events-none">
                <div className="bg-white/10 backdrop-blur-lg border border-white/10 px-4 py-2 rounded-full pointer-events-auto shadow-lg">
                    <span className="font-bold tracking-tight text-white">Social<span className="text-indigo-400">SEO</span></span>
                    <span className="text-white/30 mx-2">/</span>
                    <span className="text-white/70 font-medium text-sm">Leo's Pride v2</span>
                </div>
                <div className="bg-indigo-500/20 backdrop-blur-lg border border-indigo-500/30 px-3 py-1 rounded-full pointer-events-auto">
                    <span className="text-indigo-300 text-xs font-bold tracking-widest uppercase">Proposal</span>
                </div>
            </header>


            <main className="max-w-[1120px] mx-auto px-4 space-y-28">

                {/* ═══════ HERO ═══════ */}
                <section className="text-center pt-10">
                    <SectionTag icon={Rocket} label="Production Retainer & Partnership v2" />
                    <h1 className="text-5xl md:text-7xl font-black tracking-tight mb-6 bg-clip-text text-transparent bg-gradient-to-r from-white via-indigo-200 to-indigo-400 leading-tight pb-2">
                        30 animated shorts.<br />Every month. $2,500.
                    </h1>
                    <p className="text-xl md:text-2xl text-white/60 max-w-3xl mx-auto leading-relaxed mb-4">
                        Or $2,000/mo with a 3-month prepay.
                    </p>
                    <p className="text-lg md:text-xl text-white/50 max-w-3xl mx-auto leading-relaxed mb-12">
                        Building Leo's Pride into a kids' IP. Fast, cheap, scalable, with AI.
                    </p>

                    {/* Hero Stats */}
                    <div className="grid grid-cols-2 md:grid-cols-4 max-w-3xl mx-auto gap-4 mt-8">
                        <StatCard value="30" label="Shorts / month" sub="~45s each" accent="indigo" />
                        <StatCard value="~$83" label="Per short" sub="All-in production" accent="emerald" />
                        <StatCard value="Weekly" label="Batch delivery" sub="7–8 shorts/week" accent="purple" />
                        <StatCard value="1–2" label="Revision rounds" sub="Per short, included" accent="amber" />
                    </div>
                </section>


                {/* ═══════ THE SAVINGS ═══════ */}
                <section>
                    <div className="text-center mb-12">
                        <SectionTag icon={Scale} label="The Savings" color="rose" />
                        <h2 className="text-4xl font-black mb-4">What 30 shorts/month actually costs</h2>
                        <p className="text-slate-400 max-w-2xl mx-auto mb-8">
                            Same volume. Same quality bar. Wildly different price tags.
                        </p>
                    </div>

                    <div className="grid lg:grid-cols-3 gap-6 max-w-[1100px] mx-auto">

                        {/* Option 1: Traditional Studio */}
                        <div className="rounded-2xl border border-slate-700/70 bg-slate-800/30 p-8 flex flex-col relative opacity-80 scale-95">
                            <h3 className="text-xl font-bold mb-2 text-slate-300">1. Traditional<br />Animation Studio</h3>
                            <div className="text-3xl font-black text-rose-400 my-4">$15K – $50K</div>
                            <div className="text-sm font-semibold text-slate-400 uppercase tracking-widest mb-6">Per Month</div>
                            <ul className="space-y-3 flex-grow mb-6">
                                <Cross>$500+ per short, 30 shorts a month.</Cross>
                                <Cross>4–6 weeks per batch, not weekly.</Cross>
                                <Cross>You manage writers, animators, sound designers.</Cross>
                                <Cross>Annual cost: $180K to $600K.</Cross>
                            </ul>
                            <div className="mt-auto py-3 bg-rose-500/10 rounded-lg text-center border border-rose-500/20 text-rose-400 font-bold text-sm">
                                Too slow. Too expensive.
                            </div>
                        </div>

                        {/* Option 2: Solo Freelancer */}
                        <div className="rounded-2xl border border-slate-700/70 bg-slate-800/30 p-8 flex flex-col relative opacity-80 scale-95">
                            <h3 className="text-xl font-bold mb-2 text-slate-300">2. Solo AI<br />Freelancer</h3>
                            <div className="text-3xl font-black text-amber-400 my-4">$9,000</div>
                            <div className="text-sm font-semibold text-slate-400 uppercase tracking-widest mb-6">Per Month</div>
                            <ul className="space-y-3 flex-grow mb-6">
                                <Cross>$300 per short, 30 shorts a month.</Cross>
                                <Cross>One person bottleneck, no batch system.</Cross>
                                <Cross>Inconsistent output, missed deadlines.</Cross>
                                <Cross>Annual cost: $108K.</Cross>
                            </ul>
                            <div className="mt-auto py-3 bg-amber-500/10 rounded-lg text-center border border-amber-500/20 text-amber-400 font-bold text-sm">
                                Single point of failure.
                            </div>
                        </div>

                        {/* Option 3: SocialSEO Leo's Pride Deal */}
                        <div className="rounded-2xl border border-emerald-500/50 bg-emerald-900/10 p-8 flex flex-col relative shadow-[0_0_40px_-10px_rgba(16,185,129,0.3)] z-10 scale-105 transform">
                            <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-emerald-500 text-slate-900 text-xs font-bold px-4 py-1.5 rounded-full uppercase tracking-widest shadow-lg">
                                This Deal
                            </div>
                            <h3 className="text-xl font-bold mb-2 text-white mt-2">3. SocialSEO<br />Leo's Pride Retainer</h3>
                            <div className="text-4xl font-black text-emerald-400 my-4">$2,500</div>
                            <div className="text-sm font-semibold text-emerald-400/80 uppercase tracking-widest mb-6">Per Month</div>
                            <ul className="space-y-3 flex-grow mb-6">
                                <Check>30 shorts, end-to-end, ~$83 each.</Check>
                                <Check>Weekly batches, tight feedback loop.</Check>
                                <Check>Animation, voice, sound, editing, all in.</Check>
                                <Check>Annual cost: $30K. Save $10K–$45K every month.</Check>
                            </ul>
                            <div className="mt-auto py-3 bg-emerald-50 text-slate-900 rounded-lg text-center font-bold text-sm shadow-md">
                                Unbeatable ROI.
                            </div>
                        </div>

                    </div>

                    <div className="text-center mt-10">
                        <p className="text-2xl md:text-3xl font-black bg-clip-text text-transparent bg-gradient-to-r from-emerald-300 to-emerald-500">
                            You save $10K to $45K every month.
                        </p>
                        <p className="text-slate-400 text-sm mt-2">Same volume. Same quality. Faster turnaround.</p>
                    </div>
                </section>


                {/* ═══════ WHAT YOU GET ═══════ */}
                <section>
                    <div className="text-center mb-12">
                        <SectionTag icon={Layers} label="Every Month" />
                        <h2 className="text-4xl font-black mb-4">What you get</h2>
                        <p className="text-slate-400 max-w-2xl mx-auto">
                            End-to-end production. No add-ons. No surprises.
                        </p>
                    </div>

                    <div className="grid md:grid-cols-2 gap-x-12 gap-y-8 max-w-5xl mx-auto bg-slate-900/40 p-8 md:p-12 rounded-3xl border border-white/5">

                        <div>
                            <div className="flex items-center gap-3 mb-4 text-indigo-300">
                                <Film className="w-6 h-6" />
                                <h3 className="text-xl font-bold text-white">30 Shorts, ~45s each</h3>
                            </div>
                            <ul className="space-y-3 pl-9">
                                <Check><strong>Format experiments built in:</strong> multiple styles tested in parallel so we find the winners fast.</Check>
                                <Check><strong>Weekly batch delivery:</strong> 7–8 shorts per week, not one big drop at month-end.</Check>
                                <Check><strong>1–2 revisions included</strong> per short, no nickel-and-diming.</Check>
                            </ul>
                        </div>

                        <div>
                            <div className="flex items-center gap-3 mb-4 text-purple-300">
                                <Wand2 className="w-6 h-6" />
                                <h3 className="text-xl font-bold text-white">Cinematic Animation</h3>
                            </div>
                            <ul className="space-y-3 pl-9">
                                <Check><strong>Character continuity locked:</strong> Leo and the Rare 7 stay on-model across every short.</Check>
                                <Check><strong>Style-pinned generation:</strong> consistent palette, lighting, and motion language.</Check>
                            </ul>
                        </div>

                        <div>
                            <div className="flex items-center gap-3 mb-4 text-emerald-300">
                                <Mic2 className="w-6 h-6" />
                                <h3 className="text-xl font-bold text-white">Voice & Sound</h3>
                            </div>
                            <ul className="space-y-3 pl-9">
                                <Check><strong>Human-grade AI voiceover</strong> with kid-friendly pacing and emotion.</Check>
                                <Check><strong>Original soundtrack</strong> generated per short, mood-matched.</Check>
                                <Check><strong>Full SFX pass</strong> on every short for retention.</Check>
                            </ul>
                        </div>

                        <div>
                            <div className="flex items-center gap-3 mb-4 text-amber-300">
                                <Pen className="w-6 h-6" />
                                <h3 className="text-xl font-bold text-white">Scripting & Post</h3>
                            </div>
                            <ul className="space-y-3 pl-9">
                                <Check><strong>Hook-first scripts</strong> built for the YouTube Shorts algorithm.</Check>
                                <Check><strong>Edit, color, captions, overlays</strong> all included.</Check>
                                <Check><strong>Notion + WhatsApp collab</strong> for live feedback.</Check>
                            </ul>
                        </div>

                    </div>
                </section>


                {/* ═══════ PRICING ═══════ */}
                <section id="plans">
                    <div className="text-center mb-10">
                        <SectionTag icon={Zap} label="Investment" />
                        <h2 className="text-4xl font-black mb-4">Pick your plan</h2>
                        <p className="text-slate-400 max-w-2xl mx-auto mb-8">
                            Same 30 shorts. Same scope. Pay monthly or lock in the discount.
                        </p>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-[900px] mx-auto">

                        {/* Month-to-month */}
                        <div className="relative rounded-2xl border-t-4 border-t-slate-400 border border-slate-700/70 bg-slate-800/50 backdrop-blur-sm p-8 flex flex-col">
                            <div className="text-center pb-6 border-b border-white/10 mb-6">
                                <h3 className="text-2xl font-bold mb-2">Month-to-Month</h3>
                                <p className="text-slate-400 text-sm">Maximum flexibility</p>
                            </div>
                            <div className="text-center mb-2">
                                <div className="text-5xl font-black text-white">$2,500</div>
                                <div className="text-sm text-white/40 mt-1">/ month</div>
                            </div>
                            <div className="text-center mb-6">
                                <span className="text-xs text-slate-300 font-semibold bg-white/5 px-3 py-1 rounded-full">~$83 / short</span>
                            </div>
                            <ul className="space-y-3 flex-grow mb-6">
                                <Check>30 shorts every month</Check>
                                <Check>Weekly batch delivery</Check>
                                <Check>1–2 revisions per short</Check>
                                <Check>Cancel anytime after month 1</Check>
                            </ul>
                            <div className="mt-auto py-3 bg-slate-500/10 rounded-lg text-center border border-slate-500/20 text-slate-300 font-bold text-sm">
                                Pay per month
                            </div>
                        </div>

                        {/* 3-month prepay */}
                        <div className="relative rounded-2xl border-t-4 border-t-emerald-500 border border-emerald-500/40 bg-emerald-900/10 backdrop-blur-sm p-8 flex flex-col shadow-[0_0_40px_-10px_rgba(16,185,129,0.3)]">
                            <div className="absolute -top-3 right-4 bg-emerald-500 text-slate-900 text-xs font-bold px-3 py-1 rounded-full shadow-lg">
                                Best Value
                            </div>
                            <div className="text-center pb-6 border-b border-white/10 mb-6">
                                <h3 className="text-2xl font-bold mb-2">3-Month Prepay</h3>
                                <p className="text-emerald-300/80 text-sm">20% off, lock the rate</p>
                            </div>
                            <div className="text-center mb-2">
                                <div className="text-5xl font-black text-emerald-400">$2,000</div>
                                <div className="text-sm text-white/40 mt-1">/ month</div>
                            </div>
                            <div className="text-center mb-6">
                                <span className="text-xs text-emerald-300 font-semibold bg-emerald-500/10 px-3 py-1 rounded-full">$6,000 upfront</span>
                            </div>
                            <ul className="space-y-3 flex-grow mb-6">
                                <Check>Everything in month-to-month</Check>
                                <Check>20% discount on the retainer</Check>
                                <Check>Rate locked for 3 months</Check>
                                <Check>Same prepay structure as the first deal</Check>
                            </ul>
                            <div className="mt-auto py-3 bg-emerald-50 text-slate-900 rounded-lg text-center font-bold text-sm shadow-md">
                                Save $1,500 over 3 months
                            </div>
                        </div>

                    </div>
                </section>


                {/* ═══════ AI CREDITS ═══════ */}
                <section>
                    <div className="max-w-4xl mx-auto">
                        <div className="bg-slate-900/60 border border-amber-500/30 rounded-3xl p-8 md:p-12 shadow-2xl">
                            <div className="flex items-start gap-5">
                                <div className="bg-amber-500/20 p-3 rounded-xl text-amber-400 shrink-0">
                                    <Coins className="w-7 h-7" />
                                </div>
                                <div>
                                    <h2 className="text-2xl md:text-3xl font-black mb-3">AI Credits, Handled Separately</h2>
                                    <p className="text-slate-300 leading-relaxed mb-4">
                                        Production is one cost. The raw AI compute (Higgsfield, Freepik, ElevenLabs, premium video model credits) is the other. We've separated the two so you only ever pay for what's actually used.
                                    </p>
                                    <ul className="space-y-3">
                                        <Check><strong>$800/month budget</strong>, paid directly by you to the vendors or topped up into our shared account.</Check>
                                        <Check><strong>Unused balance rolls over</strong> to the next month or refunds back at any point.</Check>
                                        <Check><strong>Full receipt transparency</strong>, monthly breakdown of where every credit went.</Check>
                                    </ul>
                                </div>
                            </div>
                        </div>
                    </div>
                </section>


                {/* ═══════ THE PATH FORWARD ═══════ */}
                <section>
                    <div className="text-center mb-12">
                        <SectionTag icon={Workflow} label="The Plan" color="emerald" />
                        <h2 className="text-4xl font-black mb-4">Where this goes from here</h2>
                        <p className="text-slate-400 max-w-2xl mx-auto">
                            Start with shorts. Layer in musicals and the pilot once the engine is humming.
                        </p>
                    </div>

                    <div className="grid md:grid-cols-3 gap-6 max-w-5xl mx-auto">
                        <div className="rounded-2xl border border-indigo-500/30 bg-indigo-900/10 p-6">
                            <div className="text-indigo-300 text-sm font-bold uppercase tracking-widest mb-3">Month 1 · June</div>
                            <h3 className="text-xl font-bold text-white mb-3">Dial in the format</h3>
                            <p className="text-slate-300 text-sm leading-relaxed">30 shorts. Multiple variants. Find what works on YouTube Shorts. Tighten the feedback loop.</p>
                        </div>
                        <div className="rounded-2xl border border-purple-500/30 bg-purple-900/10 p-6">
                            <div className="text-purple-300 text-sm font-bold uppercase tracking-widest mb-3">Month 2 · July</div>
                            <h3 className="text-xl font-bold text-white mb-3">Scale + musicals</h3>
                            <p className="text-slate-300 text-sm leading-relaxed">New hire fully trained. Option to scale to 60 shorts and add musical episodes. Priced in v3 after month 1 data.</p>
                        </div>
                        <div className="rounded-2xl border border-emerald-500/30 bg-emerald-900/10 p-6">
                            <div className="text-emerald-300 text-sm font-bold uppercase tracking-widest mb-3">Month 3 · August</div>
                            <h3 className="text-xl font-bold text-white mb-3">Pilot completion</h3>
                            <p className="text-slate-300 text-sm leading-relaxed">Add the long-form pilot episode as a separate scope. Full IP showcase. Priced in v3.</p>
                        </div>
                    </div>

                    <p className="text-center text-slate-400 text-sm mt-8 max-w-2xl mx-auto">
                        Shorts are the engine. Musicals are the recall asset. The pilot is the showcase. As volume grows, the retainer scales, full plan in v3 after month 1 results.
                    </p>
                </section>


                {/* ═══════ THE AI ADVANTAGE ═══════ */}
                <section>
                    <div className="text-center mb-12">
                        <SectionTag icon={Sparkles} label="Why This Works" color="indigo" />
                        <h2 className="text-4xl font-black mb-4">The AI advantage</h2>
                    </div>

                    <div className="grid md:grid-cols-2 gap-6 max-w-5xl mx-auto">
                        <div className="rounded-2xl border border-white/10 bg-slate-900/40 p-6">
                            <div className="flex items-center gap-3 mb-3">
                                <Zap className="w-6 h-6 text-indigo-400" />
                                <h3 className="text-lg font-bold">10x faster iteration</h3>
                            </div>
                            <p className="text-slate-300 text-sm leading-relaxed">Traditional pipelines need weeks per short. Ours runs weekly. That means 30 chances per month to find a viral hit, not 3.</p>
                        </div>
                        <div className="rounded-2xl border border-white/10 bg-slate-900/40 p-6">
                            <div className="flex items-center gap-3 mb-3">
                                <TrendingUp className="w-6 h-6 text-emerald-400" />
                                <h3 className="text-lg font-bold">Volume = virality math</h3>
                            </div>
                            <p className="text-slate-300 text-sm leading-relaxed">Test 30 ideas a month. Find what's working. Double down. The 2.25M-sub Indian kids' IP did this manually. We do it with AI at 5% of the cost.</p>
                        </div>
                        <div className="rounded-2xl border border-white/10 bg-slate-900/40 p-6">
                            <div className="flex items-center gap-3 mb-3">
                                <Layers className="w-6 h-6 text-purple-400" />
                                <h3 className="text-lg font-bold">Format experimentation built in</h3>
                            </div>
                            <p className="text-slate-300 text-sm leading-relaxed">We can run 5 different visual styles, 3 hook structures, and 2 voiceover treatments in a single month. Data tells us the winners.</p>
                        </div>
                        <div className="rounded-2xl border border-white/10 bg-slate-900/40 p-6">
                            <div className="flex items-center gap-3 mb-3">
                                <Wand2 className="w-6 h-6 text-amber-400" />
                                <h3 className="text-lg font-bold">Character + style consistency</h3>
                            </div>
                            <p className="text-slate-300 text-sm leading-relaxed">Leo, Bebe, and the Rare 7 stay on-model across every short. Same crown, same braided mane, same glow marks. That's the IP moat.</p>
                        </div>
                    </div>
                </section>


                {/* ═══════ TIMELINE ═══════ */}
                <section>
                    <div className="text-center mb-12">
                        <SectionTag icon={Calendar} label="Timeline" color="amber" />
                        <h2 className="text-4xl font-black mb-4">From signed to shipping</h2>
                    </div>

                    <div className="max-w-3xl mx-auto relative border-l border-white/10 ml-4 md:ml-6 space-y-8 pb-4">
                        <div className="relative pl-8 md:pl-10">
                            <div className="absolute -left-[14px] top-1 w-7 h-7 bg-emerald-500 rounded-full flex items-center justify-center border-[4px] border-[#07090E] shadow-[0_0_15px_rgba(16,185,129,0.5)]">
                                <span className="text-white text-xs font-bold">1</span>
                            </div>
                            <h3 className="text-xl font-bold mb-1 text-white">Contract signed</h3>
                            <p className="text-emerald-400/80 text-sm font-semibold uppercase tracking-wider">Today</p>
                        </div>
                        <div className="relative pl-8 md:pl-10">
                            <div className="absolute -left-[14px] top-1 w-7 h-7 bg-indigo-500 rounded-full flex items-center justify-center border-[4px] border-[#07090E] shadow-[0_0_15px_rgba(99,102,241,0.5)]">
                                <span className="text-white text-xs font-bold">2</span>
                            </div>
                            <h3 className="text-xl font-bold mb-1 text-white">Production starts</h3>
                            <p className="text-indigo-400/80 text-sm font-semibold uppercase tracking-wider">Day after contract</p>
                        </div>
                        <div className="relative pl-8 md:pl-10">
                            <div className="absolute -left-[14px] top-1 w-7 h-7 bg-indigo-500 rounded-full flex items-center justify-center border-[4px] border-[#07090E] shadow-[0_0_15px_rgba(99,102,241,0.5)]">
                                <span className="text-white text-xs font-bold">3</span>
                            </div>
                            <h3 className="text-xl font-bold mb-1 text-white">First batch delivered</h3>
                            <p className="text-indigo-400/80 text-sm font-semibold uppercase tracking-wider">End of Week 1</p>
                        </div>
                        <div className="relative pl-8 md:pl-10">
                            <div className="absolute -left-[14px] top-1 w-7 h-7 bg-purple-500 rounded-full flex items-center justify-center border-[4px] border-[#07090E] shadow-[0_0_15px_rgba(168,85,247,0.5)]">
                                <span className="text-white text-xs font-bold">4</span>
                            </div>
                            <h3 className="text-xl font-bold mb-1 text-white">New hire onboards</h3>
                            <p className="text-purple-400/80 text-sm font-semibold uppercase tracking-wider">June 1</p>
                        </div>
                        <div className="relative pl-8 md:pl-10">
                            <div className="absolute -left-[14px] top-1 w-7 h-7 bg-purple-500 rounded-full flex items-center justify-center border-[4px] border-[#07090E] shadow-[0_0_15px_rgba(168,85,247,0.5)]">
                                <span className="text-white text-xs font-bold">5</span>
                            </div>
                            <h3 className="text-xl font-bold mb-1 text-white">Scaled production capacity</h3>
                            <p className="text-purple-400/80 text-sm font-semibold uppercase tracking-wider">June 8 onwards</p>
                        </div>
                    </div>
                </section>


                {/* ═══════ WHAT'S SEPARATE ═══════ */}
                <section>
                    <div className="text-center mb-12">
                        <SectionTag icon={Layers} label="Not in this retainer" color="amber" />
                        <h2 className="text-4xl font-black mb-4">What's priced separately</h2>
                        <p className="text-slate-400 max-w-2xl mx-auto">
                            Different cost profiles. Different scope. Priced as we add them.
                        </p>
                    </div>

                    <div className="grid md:grid-cols-3 gap-6 max-w-5xl mx-auto">
                        <div className="rounded-2xl border border-white/10 bg-slate-900/40 p-6">
                            <h3 className="text-lg font-bold text-white mb-2">Musical episodes</h3>
                            <p className="text-slate-400 text-sm leading-relaxed">Longer, song-driven episodes for channel recall. Priced in v3 next month.</p>
                        </div>
                        <div className="rounded-2xl border border-white/10 bg-slate-900/40 p-6">
                            <h3 className="text-lg font-bold text-white mb-2">Pilot episode</h3>
                            <p className="text-slate-400 text-sm leading-relaxed">Long-form showcase of the full IP. Priced in v3 once shorts are dialed.</p>
                        </div>
                        <div className="rounded-2xl border border-white/10 bg-slate-900/40 p-6">
                            <h3 className="text-lg font-bold text-white mb-2">Eureka digital marketing</h3>
                            <p className="text-slate-400 text-sm leading-relaxed">Separate retainer for the biscuit brand. Scope and budget aligned independently.</p>
                        </div>
                    </div>
                </section>


                {/* ═══════ EQUITY & PARTNERSHIP ═══════ */}
                <section>
                    <div className="max-w-4xl mx-auto">
                        <div className="bg-gradient-to-br from-indigo-900/30 to-slate-900/80 border border-indigo-500/30 rounded-3xl p-8 md:p-10">
                            <div className="flex items-start gap-5">
                                <div className="bg-indigo-500/20 p-3 rounded-xl text-indigo-400 shrink-0">
                                    <Handshake className="w-7 h-7" />
                                </div>
                                <div>
                                    <h2 className="text-2xl md:text-3xl font-black mb-3">Equity & partnership</h2>
                                    <p className="text-slate-300 leading-relaxed">
                                        The production retainer above is the foundation. Equity in the future Leo's Pride LLC is the partnership. Those terms are being finalized separately and live in the partnership agreement v1, which we'll circulate alongside this proposal.
                                    </p>
                                </div>
                            </div>
                        </div>
                    </div>
                </section>


                {/* ═══════ FOOTER CTA ═══════ */}
                <section className="text-center pb-10">
                    <div className="max-w-2xl mx-auto bg-gradient-to-br from-indigo-900/50 to-slate-900/80 border border-indigo-500/30 rounded-3xl p-10 md:p-14 shadow-2xl">
                        <Rocket className="w-10 h-10 text-indigo-400 mx-auto mb-4" />
                        <h2 className="text-3xl md:text-4xl font-black mb-4 text-white">Accept & start June 1</h2>
                        <p className="text-slate-300 text-lg mb-2">
                            30 shorts a month. Weekly delivery. Cinematic AI animation.
                        </p>
                        <p className="text-slate-400 text-sm mb-8">
                            Suresh Malani, Social SEO
                        </p>
                        <p className="text-indigo-300 text-sm font-semibold uppercase tracking-widest">
                            Reply on WhatsApp to lock in the rate
                        </p>
                    </div>
                </section>

            </main>
        </div>
    );
}
